import * as React from "react";

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
  /** When set, this option is rendered inside an <optgroup label={group}>. Top-level options come first. */
  group?: string;
}

export interface SelectProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "value" | "onChange" | "children"
  > {
  value?: string;
  /** shadcn-style: receive the new value directly. */
  onValueChange?: (value: string) => void;
  /** Native event passthrough. If both are set, onValueChange fires first. */
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  /** Marks the field with destructive border/ring. */
  error?: boolean;
  /** Optional placeholder rendered as disabled option when `value` is empty. */
  placeholder?: string;
}

const BASE =
  "w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground disabled:bg-muted disabled:cursor-not-allowed";

const ERROR_CLASSES = "border-destructive focus:border-destructive focus:ring-destructive";

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    {
      value,
      onValueChange,
      onChange,
      options,
      error,
      placeholder,
      className,
      ...rest
    },
    ref,
  ) {
    const classes = [BASE, error ? ERROR_CLASSES : "", className]
      .filter(Boolean)
      .join(" ");

    // Partition options: top-level first, then grouped by group name (Map preserves insertion order).
    const topLevel: SelectOption[] = [];
    const grouped = new Map<string, SelectOption[]>();
    for (const opt of options) {
      if (!opt.group) {
        topLevel.push(opt);
      } else {
        const existing = grouped.get(opt.group);
        if (existing) existing.push(opt);
        else grouped.set(opt.group, [opt]);
      }
    }

    return (
      <select
        ref={ref}
        value={value ?? ""}
        onChange={(e) => {
          onValueChange?.(e.target.value);
          onChange?.(e);
        }}
        className={classes}
        {...rest}
      >
        {placeholder !== undefined && (
          <option value="" disabled hidden>
            {placeholder}
          </option>
        )}
        {topLevel.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
        {Array.from(grouped.entries()).map(([groupName, opts]) => (
          <optgroup key={groupName} label={groupName}>
            {opts.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    );
  },
);
